import {
  Input,
  input,
  inject,
  OnInit,
  Output,
  computed,
  Component,
  OnDestroy,
  EventEmitter,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  faBars,
  faGear,
  faPlus,
  faTrash,
  faMinus,
  faAngleLeft,
  faAngleRight,
  faMagnifyingGlass,
  faCircleStop,
} from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faClock,
  faCalendar,
  faCirclePlay,
} from '@fortawesome/free-regular-svg-icons';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import {
  DialogService,
  DialogCloseOptions,
  DialogCloseReason,
} from '@sui/dialog';

import { ConfirmDialogComponent } from '../popups/confirm-dialog/confirm-dialog.component';
import { DashboardPagePersistenceService } from '../../services/dashboard-page-persistence.service';

import {
  DashboardPage,
  DashboardPresentationSettings,
} from '../../models/core.models';

import {
  setDashboardPagesSelection,
  toggleDashboardPageSelection,
  clearDashboardPageSelection,
} from '../../actions/object.actions';

import { selectSelectedDashboardPageIds } from '../../reducers';
import { AppState } from 'src/app/reducers';
import {
  injectDashboardUIStateQuery,
  injectUpdateDashboardUIState,
} from 'src/app/api/queries/view-preset.queries';

import { Observable, take } from 'rxjs';

@Component({
  selector: 'app-page-navigator-for-dashboard',
  templateUrl: './page-navigator-for-dashboard.component.html',
  styleUrls: ['./page-navigator-for-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PageNavigatorForDashboardComponent implements OnInit, OnDestroy {
  @HostListener('document:keydown.escape', ['$event' as any])
  handleEscape(event: KeyboardEvent) {
    this.store.dispatch(clearDashboardPageSelection());
  }

  @Input() pages: DashboardPage[] = [];
  @Input() presentationSettings: DashboardPresentationSettings | null = null;
  @Input() selectedPageId: number | null = null;
  /**
   * A signal input because the `ov-dashboard` query is keyed on it. Read as
   * `dashboardId()` everywhere.
   */
  readonly dashboardId = input<string | null>(null);
  @Input() allowAdd = true;
  @Input() allowReorder = false;
  @Input() addButtonText = 'Add New Page';
  @Input() searchPlaceholder = 'Search';
  @Input() title = 'Dashboard Pages';

  @Output() pageSelected = new EventEmitter<number>();
  @Output() pageAdded = new EventEmitter<void>();
  @Output() pageDeleted = new EventEmitter<number>();
  @Output() pageRenamed = new EventEmitter<{ id: number; name: string }>();
  @Output() pageVisibilityToggled = new EventEmitter<{
    pageId: number;
    visible: boolean;
  }>();
  @Output() pageReordered = new EventEmitter<{
    orderedIds: number[];
    orderedPages: DashboardPage[];
    rollbackPages: DashboardPage[];
  }>();

  faGear = faGear;
  faBars = faBars;
  faPlus = faPlus;
  faClock = faClock;
  faTrash = faTrash;
  faMinus = faMinus;
  faCalendar = faCalendar;
  faAngleLeft = faAngleLeft;
  faCircleStop = faCircleStop;
  faAngleRight = faAngleRight;
  faCirclePlay = faCirclePlay;
  faMagnifyingGlass = faMagnifyingGlass;

  selectedDashboardPageIds$: Observable<number[]>;

  searchQuery = '';
  isReordering = false;

  private store = inject(Store<AppState>);
  private translateService = inject(TranslateService);
  private dialogService = inject(DialogService);
  private pagePersistence = inject(DashboardPagePersistenceService);

  // * the `ov-dashboard` view preset; this component is its only reader
  private readonly uiStateQuery = injectDashboardUIStateQuery(this.dashboardId);
  private readonly updateUIState = injectUpdateDashboardUIState();

  /** Collapsed is the *absence* of `pagination_menu_open`. */
  readonly isCollapsed = computed(
    () => !(this.uiStateQuery.data()?.pagination_menu_open ?? false),
  );

  ngOnInit(): void {
    this.selectedDashboardPageIds$ = this.store.select(
      selectSelectedDashboardPageIds,
    );
  }

  ngOnDestroy(): void {
    this.store.dispatch(clearDashboardPageSelection());
  }

  get filteredPages(): DashboardPage[] {
    if (!this.searchQuery.trim()) {
      return this.pages || [];
    }

    return (this.pages || []).filter((page) =>
      page.name.toLowerCase().includes(this.searchQuery.toLowerCase()),
    );
  }

  get hasActiveSearch(): boolean {
    return this.searchQuery.trim().length > 0;
  }

  // * collapse functionality
  getToggleIcon(isCollapsed: boolean): IconProp {
    return isCollapsed ? this.faAngleRight : this.faAngleLeft;
  }

  onPageClick(pageId: number, event?: MouseEvent): void {
    if (event && (event.ctrlKey || event.metaKey)) {
      // * Ctrl + Click: Toggle individual selection
      this.store.dispatch(toggleDashboardPageSelection({ pageId }));
      return;
    }

    if (event && event.shiftKey) {
      // * Shift + Click: Range selection
      this.handleRangeSelection(pageId);
      return;
    }

    // * Normal click: Clear selection and select page for navigation
    this.store.dispatch(clearDashboardPageSelection());
    this.pageSelected.emit(pageId);
  }

  private handleRangeSelection(clickedPageId: number): void {
    this.selectedDashboardPageIds$.pipe(take(1)).subscribe((selectedIds) => {
      if (selectedIds.length === 0) {
        this.store.dispatch(
          setDashboardPagesSelection({ pageIds: [clickedPageId] }),
        );
        return;
      }

      const lastSelectedId = selectedIds[selectedIds.length - 1];
      const lastIndex = this.pages.findIndex((p) => p.id === lastSelectedId);
      const clickedIndex = this.pages.findIndex((p) => p.id === clickedPageId);

      if (lastIndex === -1 || clickedIndex === -1) return;

      // * select ALL Pages in Range
      const start = Math.min(lastIndex, clickedIndex);
      const end = Math.max(lastIndex, clickedIndex);
      const rangePageIds = this.pages.slice(start, end + 1).map((p) => p.id);

      const newSelection = Array.from(
        new Set([...selectedIds, ...rangePageIds]),
      );
      this.store.dispatch(
        setDashboardPagesSelection({ pageIds: newSelection }),
      );
    });
  }

  onDeletePageClick(pageId: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    const message = this.translateService.instant(
      'page-navigator.delete-action.confirmation-message',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.pageDeleted.emit(pageId);
        } else if (event.reason === DialogCloseReason.Cancel) {
        }
      },
    );
  }

  onSettingsClick(): void {
    // TODO: find out what was the goal of this as UX - open the settings popup?
  }

  onSearchFilterChange(searchQuery: string): void {
    this.searchQuery = searchQuery;
  }

  toggleCollapse(): void {
    const dashboardId = this.dashboardId();
    if (!dashboardId) return;

    this.updateUIState(dashboardId, {
      pagination_menu_open: this.isCollapsed(),
    });
  }

  createPage(): void {
    this.pageAdded.emit();
  }

  isPageVisible(page: DashboardPage): boolean {
    return page.is_visible_in_presentation === true;
  }

  isPageMultiSelected(page: DashboardPage, selectedIds: number[]): boolean {
    return selectedIds.includes(page.id);
  }

  /**
   * True when the selection covers every page — a dashboard must keep at least
   * one, so `deleteSelectedPages` refuses. Bound to the menu item's `disabled` so
   * the refusal is visible instead of silent.
   */
  allPagesSelected(selectedIds: number[] | null): boolean {
    const pageCount = this.pages?.length ?? 0;

    return !!selectedIds?.length && selectedIds.length >= pageCount;
  }

  onVisibilityIconClick(page: DashboardPage, event: Event): void {
    event.stopPropagation(); // ! prevent Page selection

    const updatedVisibility = !page.is_visible_in_presentation;

    // * оptimistically update local State
    const pageIndex = this.pages.findIndex((p) => p.id === page.id);

    if (pageIndex !== -1) {
      this.pages = this.pages.map((page, i) => {
        if (i !== pageIndex) return page;

        return {
          ...page,
          is_visible_in_presentation: updatedVisibility,
        };
      });
    }

    this.pageVisibilityToggled.emit({
      pageId: page.id,
      visible: updatedVisibility,
    });
  }

  onDrop(event: CdkDragDrop<DashboardPage[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    if (this.hasActiveSearch) return;

    const reorderedPages = [...this.pages];
    moveItemInArray(reorderedPages, event.previousIndex, event.currentIndex);

    this.pages = reorderedPages.map((page, index) => ({
      ...page,
      page_order: index + 1,
    }));

    this.persistPageOrder(this.pages);
  }

  private persistPageOrder(fullPages: DashboardPage[]): void {
    if (!this.dashboardId()) {
      return;
    }

    const orderedIds = fullPages.map((p) => p.id);
    const orderedPages = [...fullPages];
    const rollbackPages = [...this.pages];

    this.pageReordered.emit({ orderedIds, orderedPages, rollbackPages });
  }

  includeInPresentation(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.selectedDashboardPageIds$.pipe(take(1)).subscribe((selectedIds) => {
      // TODO: we shall have a single Endpoint to update multiple pages
      // TODO: for now we iterate and dispatch a separate action for each page
      selectedIds.map((id) => {
        const page = this.pages.find((p) => p.id === id);

        if (!page || page?.is_visible_in_presentation) return;

        this.pagePersistence.updatePage({
          dashboard_id: this.dashboardId()!,
          page_id: id,
          is_visible_in_presentation: true,
        });
      });

      this.store.dispatch(clearDashboardPageSelection());
    });
  }

  excludeFromPresentation(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.selectedDashboardPageIds$.pipe(take(1)).subscribe((selectedIds) => {
      // TODO: we shall have a single Endpoint to update multiple pages
      // TODO: for now we iterate and dispatch a separate action for each page
      selectedIds.map((id) => {
        const page = this.pages.find((p) => p.id === id);

        if (!page || page?.is_visible_in_presentation === false) return;

        this.pagePersistence.updatePage({
          dashboard_id: this.dashboardId()!,
          page_id: id,
          is_visible_in_presentation: false,
        });

        this.store.dispatch(clearDashboardPageSelection());
      });
    });
  }

  deleteSelectedPages(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.selectedDashboardPageIds$.pipe(take(1)).subscribe((selectedIds) => {
      if (!selectedIds.length || !this.dashboardId()) return;

      // * Safety check: prevent deletion if all pages would be deleted
      if (selectedIds.length >= this.pages.length) return;

      const message = this.translateService.instant(
        'page-navigator.delete-action.confirmation-message',
      );

      const dialogRef = this.dialogService.openSimpleDialog(
        ConfirmDialogComponent,
        {
          data: {
            message,
          },
          closeOnClick: false,
        },
      );

      dialogRef.closed.subscribe(
        async (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
          if (event.reason === DialogCloseReason.Confirm) {
            this.store.dispatch(clearDashboardPageSelection());

            // Sequential by design: each response carries the whole remaining
            // page list, so parallel deletes can race and resurrect pages (WAP-1585)
            for (const pageId of selectedIds) {
              await this.pagePersistence.deletePage(this.dashboardId()!, pageId);
            }
          }
        },
      );
    });
  }
}
